import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormControl, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { toPriceNumber } from '@gelato-api-ui/core/product-prices/helpers/toPriceNumber';
import { PlatformCost, PlatformCostType } from '../types/platform-cost';

@Component({
  selector: 'gd-platform-cost-control',
  templateUrl: './platform-cost-control.component.html',
  styleUrls: ['./platform-cost-control.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: PlatformCostControlComponent,
      multi: true,
    },
  ],
})
export class PlatformCostControlComponent implements ControlValueAccessor, OnInit, OnDestroy {
  @Input() currency: string;
  @Input() formControlName: string;

  readonly platformCostType = PlatformCostType;

  platformCostForm: FormGroup = this.formBuilder.group({
    costType: new FormControl(),
    amount: new FormControl(),
    percentage: new FormControl(),
    mixedAmount: new FormControl(),
    mixedPercentage: new FormControl(),
  });

  private ngOnDestroy$ = new Subject();
  private platformCostOptions: PlatformCost[] = [];

  constructor(private readonly formBuilder: FormBuilder) {}

  ngOnInit() {
    this.platformCostForm
      .get('costType')
      .valueChanges.pipe(takeUntil(this.ngOnDestroy$))
      .subscribe((type: PlatformCostType) => this.handleCostTypeChange(type));
  }

  ngOnDestroy() {
    this.ngOnDestroy$.next(true);
    this.ngOnDestroy$.complete();
  }

  onChange = (range: any) => {};
  onTouched: any = () => {};

  writeValue(value: PlatformCost[] = []): void {
    this.platformCostOptions = [...value];
    this.setInitialValue();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  isOptionSelected(type: PlatformCostType): boolean {
    return this.platformCostForm.get('costType').value === type;
  }

  onPercentageChange(value: string) {
    this.updateCost({ value: Number(value), type: PlatformCostType.Percentage });
  }

  onAmountChange(value: string) {
    this.updateCost({ value: Number(value), type: PlatformCostType.FixedAmount });
  }

  onMixedPercentageChange(value: string) {
    this.updateCost({ value: Number(value), type: PlatformCostType.Mixed });
  }

  onMixedAmountChange(value: string) {
    this.updateCost({ extraAmount: Number(value), type: PlatformCostType.Mixed });
  }

  private handleCostTypeChange(type: PlatformCostType) {
    this.platformCostOptions.forEach(option => (option.selected = option.type === type));

    this.onChange(this.platformCostOptions);
  }

  private setInitialValue() {
    const selectedCost = this.platformCostOptions.find(({ selected }) => selected);

    this.platformCostOptions.forEach(cost => {
      if (cost.type === PlatformCostType.Percentage) {
        this.platformCostForm.get('percentage').patchValue(cost.value);
      } else if (cost.type === PlatformCostType.FixedAmount) {
        this.platformCostForm.get('amount').patchValue(cost.value);
      } else if (cost.type === PlatformCostType.Mixed) {
        this.platformCostForm.get('mixedPercentage').patchValue(cost.value);
        this.platformCostForm.get('mixedAmount').patchValue(cost.extraAmount);
      }
    });

    this.platformCostForm.patchValue({
      costType: selectedCost ? selectedCost.type : this.platformCostType.Percentage,
    });
  }

  private updateCost(cost: Partial<PlatformCost>) {
    const costIndex = this.platformCostOptions.findIndex(({ type }) => type === cost.type);
    const costToUpdate = this.platformCostOptions[costIndex];
    if ([PlatformCostType.Percentage, PlatformCostType.FixedAmount].includes(costToUpdate.type)) {
      this.platformCostOptions[costIndex].value = toPriceNumber(cost.value);
    } else if (costToUpdate.type === PlatformCostType.Mixed) {
      costToUpdate.value = cost.value ? toPriceNumber(cost.value) : costToUpdate.value;
      costToUpdate.extraAmount = cost.extraAmount ? toPriceNumber(cost.extraAmount) : costToUpdate.extraAmount;
    }

    this.onChange(this.platformCostOptions);
  }
}
